import { useTranslation } from 'react-i18next';

import { Check, ChevronRight } from 'lucide-react';

import { cn } from '@codes/ui-kit';

export type BrandVariant = 'aws' | 'gemini' | 'openai' | 'default';

const brandStyles: Record<BrandVariant, { bg: string; border: string; iconBg: string; accent: string }> = {
    aws: {
        bg: 'hover:bg-amber-50/50 dark:hover:bg-amber-950/20',
        border: 'hover:border-amber-200 dark:hover:border-amber-800/50',
        iconBg: 'bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-950/40 dark:to-orange-950/40 ring-1 ring-amber-200/60 dark:ring-amber-700/40',
        accent: 'text-amber-700 dark:text-amber-400',
    },
    gemini: {
        bg: 'hover:bg-blue-50/50 dark:hover:bg-blue-950/20',
        border: 'hover:border-blue-200 dark:hover:border-blue-800/50',
        iconBg: 'bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/40 dark:to-purple-950/40 ring-1 ring-blue-200/60 dark:ring-blue-700/40',
        accent: 'text-blue-700 dark:text-blue-400',
    },
    openai: {
        bg: 'hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20',
        border: 'hover:border-emerald-200 dark:hover:border-emerald-800/50',
        iconBg: 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/40 ring-1 ring-emerald-200/60 dark:ring-emerald-700/40',
        accent: 'text-emerald-700 dark:text-emerald-400',
    },
    default: {
        bg: 'hover:bg-muted/50',
        border: 'hover:border-border',
        iconBg: 'bg-point/10 ring-1 ring-point/20',
        accent: 'text-point',
    },
};

interface KeyCardProps {
    title: string;
    description: string;
    isRegistered: boolean;
    onManage: () => void;
    icon: React.ReactNode;
    brand?: BrandVariant;
}

export const KeyCard = ({ title, description, isRegistered, onManage, icon, brand = 'default' }: KeyCardProps) => {
    const { t } = useTranslation();
    const styles = brandStyles[brand];

    return (
        <button
            type="button"
            onClick={onManage}
            className={cn(
                'w-full text-left rounded-xl border border-border bg-card p-4 md:p-5',
                'transition-all duration-200 ease-out',
                'group cursor-pointer',
                styles.bg,
                styles.border
            )}
        >
            <div className="flex items-center gap-3.5 md:gap-4">
                {/* Brand Icon */}
                <div
                    className={cn(
                        'w-11 h-11 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0',
                        'transition-transform duration-200 group-hover:scale-105',
                        styles.iconBg
                    )}
                >
                    <div className="w-6 h-6 md:w-7 md:h-7">{icon}</div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-0.5">
                        <h3 className="text-sm md:text-[15px] font-semibold text-text truncate">{title}</h3>
                        {isRegistered ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full ring-1 ring-emerald-200/60 dark:ring-emerald-700/40 whitespace-nowrap">
                                <Check className="w-3 h-3" />
                                {t('common.registered')}
                            </span>
                        ) : (
                            <span className="inline-flex items-center text-[11px] font-medium text-sub bg-muted px-2 py-0.5 rounded-full ring-1 ring-border whitespace-nowrap">
                                {t('common.notRegistered') || 'Not set'}
                            </span>
                        )}
                    </div>
                    <p className="text-xs md:text-[13px] text-sub leading-relaxed line-clamp-1">{description}</p>
                </div>

                {/* Action Arrow */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="hidden sm:inline text-xs font-medium text-sub group-hover:text-text transition-colors">
                        {isRegistered ? t('common.manage') : t('common.register')}
                    </span>
                    <ChevronRight className="w-4 h-4 text-sub/50 group-hover:text-text group-hover:translate-x-0.5 transition-all" />
                </div>
            </div>
        </button>
    );
};
